// ============================================================
// requiredFields.ts — what the case page's required-fields row shows.
// Pure: takes validateCase()'s output (the SAME rules as before — this file
// only names, orders and filters what's missing) and turns it into chips:
// section, a short field name, and the field's data-field key so a click
// can focus it.
// ============================================================
import { validateCase, type ValidationError } from '../../utils/validation'
import type { Case, ViewingPaneHeading } from '../../types/case.types'
import type { CaseDeadline } from '../../types/deadlines.types'
import { CASE_NAV_INDEX } from '../../constants/caseNav.constants'
import { caseMissingLimitation } from '../../utils/missingLimitation'
import { noTimeLimitOf } from '../case/caseFacts'

export interface MissingField {
  section: ViewingPaneHeading
  sectionLabel: string
  field: string
  label: string
  dataField?: string
}

// Page order (the case menu's order).
const SECTION_ORDER: ViewingPaneHeading[] = ['clientSource', 'clientDetails', 'accidentDetails', 'injuryDetails']

// Validation key → chip name + data-field key (FormField fieldKey).
const FIELDS: Record<string, { label: string; dataField?: string }> = {
  clientSourceName: { label: 'Source name', dataField: 'clientSource.client-source-name' },
  clientSourceContactNumber: { label: 'Contact number', dataField: 'clientSource.contact-number' },
  clientSourceEmailAddress: { label: 'Email address', dataField: 'clientSource.email-address' },
  clientSourceAddressPostcode: { label: 'Postcode', dataField: 'clientSource.postcode' },
  forename: { label: 'Forename', dataField: 'clientDetails.forename' },
  surname: { label: 'Surname', dataField: 'clientDetails.surname' },
  dateOfBirth: { label: 'Date of birth', dataField: 'clientDetails.dateOfBirth' },
  niNumber: { label: 'NI number', dataField: 'clientDetails.niNumber' },
  mainContactNumber: { label: 'Main contact number', dataField: 'clientDetails.mainContactNumber' },
  emailAddress: { label: 'Email address', dataField: 'clientDetails.emailAddress' },
  accidentDate: { label: 'Date of accident', dataField: 'accident.accident-date' },
  accidentLocation: { label: 'Accident location', dataField: 'accident.accident-location' },
  claimantReg: { label: 'Claimant registration', dataField: 'accident.claimant-reg' },
  injuries: { label: 'At least one injury', dataField: 'injuries.injuries' },
  // B3 — "Other (describe)" ticked without a description.
  injuryOtherDetails: { label: 'Other injury details', dataField: 'injuries.injuries' },
  // B2 — Case control → Claim (ClaimCard CLAIM_TYPE_DATA_FIELD).
  claimType: { label: 'Claim type', dataField: 'automatic.claim-type' },
}

// A field we don't have a name for: its message, minus "is required".
const labelFromMessage = (m: string): string => m.replace(/ is required$| must be selected$/, '')

export const sectionLabelOf = (s: ViewingPaneHeading): string => CASE_NAV_INDEX[s]?.label ?? s

export const missingFields = (errors: Record<string, ValidationError[]>): MissingField[] => {
  const keys = [...SECTION_ORDER, ...Object.keys(errors).filter((k) => !SECTION_ORDER.includes(k as ViewingPaneHeading))] as ViewingPaneHeading[]
  return keys.flatMap((section) => (errors[section] ?? []).map((e) => {
    const known = FIELDS[e.field]
    // A filled-in but badly formatted NI number is "missing" too — say so.
    const label = known
      ? (e.field === 'niNumber' && !/ is required$/.test(e.message) ? 'NI number (check format)' : known.label)
      : labelFromMessage(e.message)
    return { section, sectionLabel: sectionLabelOf(section), field: e.field, label, dataField: known?.dataField }
  }))
}

// Only what the user can fill in NOW: a field in a locked section ("Complete
// Client details first") or one hidden from the case menu is left out
// entirely — no locked chips. ★ (Audit) EXCEPT the section the page is open
// on: opened straight onto it (a link, a refresh) its own fields are on
// screen and can be filled in, so they're listed.
export const fillableFields = (
  all: MissingField[],
  lockReasonByKey: Partial<Record<ViewingPaneHeading, string>> = {},
  visibleKeys?: string[],
  openSection?: string | null,
): MissingField[] => all.filter((f) => (!lockReasonByKey[f.section] || f.section === openSection) && (!visibleKeys || visibleKeys.includes(f.section)))

// Folded: up to FOLD_AT chips show in full; past that, the first SHOW_FOLDED
// plus a "+N more" chip (so the row never grows past one short line).
export const FOLD_AT = 6
export const SHOW_FOLDED = 5
export const visibleFields = (all: MissingField[], expanded: boolean): { shown: MissingField[]; more: number } => {
  if (expanded || all.length <= FOLD_AT) return { shown: all, more: 0 }
  return { shown: all.slice(0, SHOW_FOLDED), more: all.length - SHOW_FOLDED }
}

// ★ "Limitation date": not a validateCase field — an open case with no
// limitation-type deadline at all (utils/missingLimitation) gets this chip
// too, last. It goes to Deadlines, which focuses its "Add limitation date"
// (DeadlinesSection takes the 'limitationDate' focus request).
export const LIMITATION_FIELD_KEY = 'limitationDate'
export const withLimitationField = (fields: MissingField[], missingLimitation: boolean): MissingField[] =>
  missingLimitation
    ? [...fields, { section: 'deadlines', sectionLabel: sectionLabelOf('deadlines'), field: LIMITATION_FIELD_KEY, label: 'Limitation date' }]
    : fields

// ★ (Audit) THE list — the required-fields row's chips AND the hero's
// "N fields incomplete" both come from here, so the number always equals the
// chips: validateCase's fields + the limitation chip (deadlines loaded, open
// case, none saved), minus locked / hidden sections (bar the open one).
export const requiredFieldsFor = (
  c: Case | null | undefined,
  deadlines: CaseDeadline[] | undefined,
  lockReasonByKey: Partial<Record<ViewingPaneHeading, string>> = {},
  visibleKeys?: string[],
  openSection?: string | null,
): MissingField[] => (c
  ? fillableFields(withLimitationField(missingFields(validateCase(c)), caseMissingLimitation(c.caseControl?.closeDate, deadlines, !!noTimeLimitOf(c))), lockReasonByKey, visibleKeys, openSection)
  : [])
